import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Sparkles, RefreshCcw, CheckCircle2, History, Wand2, Target, Layers,
  Hash, ClipboardList, Star, X, Loader2, Trash2, PenSquare, PieChart, Info,
  GitMerge,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";
import {
  SECTION_LABELS, SectionSourceBadge, QuickEditDialog,
  RevisionHistoryDialog, CarryOverDialog, EditPolicyPill,
} from "./BrandContentStrategyEditors";

const STAGE_TONE = {
  TOFU: "border-sky-500 text-sky-800 bg-sky-50",
  MOFU: "border-amber-500 text-amber-800 bg-amber-50",
  BOFU: "border-emerald-500 text-emerald-800 bg-emerald-50",
};
const STATUS_TONE = {
  approved: "border-emerald-500 text-emerald-800 bg-emerald-50",
  draft: "border-sky-500 text-sky-800 bg-sky-50",
  superseded: "border-border text-muted-foreground bg-muted/40",
};

export default function BrandContentStrategyTab({ brand }) {
  const wsId = brand?.workspace_id;
  const bid = brand?.id;
  const [strategy, setStrategy] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [genForm, setGenForm] = useState({
    num_topics: 30,
    include_competitor_snapshot: true,
    custom_instructions: "",
  });
  const [refineInput, setRefineInput] = useState("");
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [carryOverOpen, setCarryOverOpen] = useState(false);
  const [editSection, setEditSection] = useState(null); // section name or null
  const [editPolicy, setEditPolicy] = useState("any_member");

  const loadAll = async () => {
    if (!wsId || !bid) return;
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/latest`),
        api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/versions`),
        api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/edit-policy`)
          .catch(() => ({ data: { policy: "any_member" } })),
      ]);
      setStrategy(r1.data || null);
      setVersions(r2.data.rows || []);
      setEditPolicy(r3.data.policy || "any_member");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, [wsId, bid]);

  const updateEditPolicy = async (nextPolicy) => {
    try {
      await api.put(
        `/workspaces/${wsId}/brands/${bid}/content-strategy/edit-policy`,
        { policy: nextPolicy });
      setEditPolicy(nextPolicy);
      toast.success(`Editing policy set to "${nextPolicy === "any_member" ? "any member" : "lead / admin only"}"`);
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const openEdit = (section) => setEditSection(section);
  const handleSectionSaved = (doc) => { setStrategy(doc); };

  const generate = async () => {
    setBusy(true);
    const t = toast.loading("Generating strategy with Claude…");
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-strategy/generate`,
        genForm);
      setStrategy(r.data);
      setGenOpen(false);
      // Poll until generating=false
      await _pollUntilReady(r.data.id, t);
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Generation failed"), { id: t }); }
    finally { setBusy(false); }
  };

  const refine = async () => {
    if (!refineInput.trim()) { toast.error("Add instructions to refine"); return; }
    setBusy(true);
    const t = toast.loading("Refining with your instructions…");
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-strategy/${strategy.id}/refine`,
        { instructions: refineInput, keep_pillars: true, keep_topics_as_hints: true });
      setStrategy(r.data);
      setRefineOpen(false);
      setRefineInput("");
      await _pollUntilReady(r.data.id, t);
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Refine failed"), { id: t }); }
    finally { setBusy(false); }
  };

  const _pollUntilReady = async (sid, toastId) => {
    // Poll every 3s up to 3 minutes total
    for (let i = 0; i < 60; i++) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const r = await api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/${sid}`);
        setStrategy(r.data);
        if (!r.data.generating) {
          if (r.data.error) {
            toast.error(`Failed: ${r.data.error.slice(0, 200)}`, { id: toastId });
          } else {
            toast.success(`v${r.data.version} ready · ${(r.data.proposed_topics || []).length} topics`, { id: toastId });
          }
          return;
        }
      } catch (e) { /* keep polling */ }
    }
    toast.error("Generation timed out. Please refresh.", { id: toastId });
  };

  const approve = async () => {
    if (!strategy) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-strategy/${strategy.id}/approve`);
      setStrategy(r.data);
      toast.success(`v${r.data.version} approved`);
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Approve failed")); }
    finally { setBusy(false); }
  };

  const openVersion = async (sid) => {
    setBusy(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/${sid}`);
      setStrategy(r.data);
      setVersionsOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setBusy(false); }
  };

  const delVersion = async (sid) => {
    if (!window.confirm("Delete this version?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/brands/${bid}/content-strategy/${sid}`);
      toast.success("Version deleted");
      loadAll();
      if (strategy?.id === sid) setStrategy(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const topics = strategy?.proposed_topics || [];
  const pillars = strategy?.content_pillars || [];
  const themes = strategy?.themes || [];
  const cadence = strategy?.cadence || {};
  const hashtagBank = strategy?.hashtag_bank || {};
  const contentMix = strategy?.content_mix || {};

  const stageCounts = useMemo(() => {
    const c = { TOFU: 0, MOFU: 0, BOFU: 0 };
    topics.forEach((t) => { if (c[t.funnel_stage] !== undefined) c[t.funnel_stage]++; });
    return c;
  }, [topics]);

  return (
    <div className="space-y-4" data-testid="brand-content-strategy-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-semibold">Content Strategy</h2>
          {strategy && (
            <>
              <Badge variant="outline" className={`rounded-none text-[10px] border ${STATUS_TONE[strategy.status] || ""}`}>
                v{strategy.version} · {strategy.status}
              </Badge>
              {strategy.model && (
                <span className="text-[10px] text-muted-foreground font-mono">{strategy.model}</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {strategy && (
            <EditPolicyPill policy={editPolicy} onChange={updateEditPolicy} canManage />
          )}
          {strategy && (
            <>
              <Button variant="outline" size="sm" className="rounded-none h-8"
                        onClick={() => setRevisionOpen(true)}
                        data-testid="content-strategy-revisions-btn">
                <History className="w-3.5 h-3.5 mr-1.5" /> Revisions
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-8"
                        onClick={() => setCarryOverOpen(true)}
                        disabled={versions.length < 2}
                        title={versions.length < 2 ? "Need at least 2 versions to carry over" : "Carry manual edits from another version"}
                        data-testid="content-strategy-carry-over-btn">
                <GitMerge className="w-3.5 h-3.5 mr-1.5" /> Carry over
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-8"
                        onClick={() => setVersionsOpen(true)}
                        data-testid="content-strategy-versions-btn">
                <History className="w-3.5 h-3.5 mr-1.5" /> Versions · {versions.length}
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-8"
                        onClick={() => setRefineOpen(true)} disabled={busy}
                        data-testid="content-strategy-refine-btn">
                <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Refine with feedback
              </Button>
              {strategy.status !== "approved" && (
                <Button size="sm" className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700"
                          onClick={approve} disabled={busy}
                          data-testid="content-strategy-approve-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve
                </Button>
              )}
            </>
          )}
          <Button size="sm" className="rounded-none h-8"
                    onClick={() => setGenOpen(true)} disabled={busy}
                    data-testid="content-strategy-generate-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {strategy ? "Regenerate" : "Generate strategy"}
          </Button>
        </div>
      </div>

      {strategy?.generating && (
        <div className="border-2 border-primary bg-primary/5 p-3 flex items-center gap-3" data-testid="content-strategy-generating">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <div className="flex-1">
            <div className="text-sm font-semibold">Claude is generating your strategy…</div>
            <div className="text-[11px] text-muted-foreground">This takes 30-60s. This page will refresh when done — safe to navigate away.</div>
          </div>
        </div>
      )}
      {strategy?.error && !strategy?.generating && (
        <div className="border-2 border-rose-400 bg-rose-50 p-3" data-testid="content-strategy-error">
          <div className="text-sm font-semibold text-rose-900">Generation failed</div>
          <div className="text-[11px] text-rose-700 mt-0.5">{strategy.error}</div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground p-8 text-center">Loading…</div>
      ) : !strategy ? (
        <div className="border border-dashed border-border p-10 text-center" data-testid="content-strategy-empty">
          <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm font-medium">No strategy yet</div>
          <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Generate a full content marketing plan using Claude Sonnet 4.5.
            Uses this brand&apos;s positioning, personas, voice, USPs, competitor snapshot and active packages.
          </div>
          <Button className="rounded-none mt-4" onClick={() => setGenOpen(true)}
                    data-testid="content-strategy-empty-generate-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Generate now
          </Button>
        </div>
      ) : (
        <>
          {/* Executive summary */}
          <div className="border-l-4 border-primary bg-primary/5 p-3" data-testid="content-strategy-summary-block">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Executive summary
              </div>
              <SectionSourceBadge meta={(strategy.section_meta || {}).summary} small />
              <button type="button"
                          onClick={() => openEdit("summary")}
                          className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                          data-testid="section-edit-summary">
                <PenSquare className="w-3 h-3" /> Edit
              </button>
            </div>
            <div className="text-sm leading-relaxed">
              {strategy.summary || <span className="text-muted-foreground italic">No summary yet</span>}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1 text-[11px]">
                <Star className="w-3 h-3 text-amber-500" />
                <span className="text-muted-foreground uppercase tracking-widest text-[9px] font-semibold">North star</span>
                <span className="font-semibold">{strategy.north_star_metric || <span className="text-muted-foreground italic">—</span>}</span>
                <SectionSourceBadge meta={(strategy.section_meta || {}).north_star_metric} small />
                <button type="button"
                            onClick={() => openEdit("north_star_metric")}
                            className="text-[10px] text-muted-foreground hover:text-primary ml-1"
                            data-testid="section-edit-north_star_metric">
                  <PenSquare className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          {/* KPIs strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
            <KpiCard label="Pillars" value={pillars.length} icon={Layers} />
            <KpiCard label="Themes" value={themes.length} icon={ClipboardList} />
            <KpiCard label="Topics" value={topics.length} icon={Target} />
            <KpiCard label="TOFU" value={stageCounts.TOFU} tone="text-sky-700" />
            <KpiCard label="MOFU" value={stageCounts.MOFU} tone="text-amber-700" />
            <KpiCard label="BOFU" value={stageCounts.BOFU} tone="text-emerald-700" />
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
            <div className="space-y-4">
              {/* Pillars */}
              <Section title="Content pillars" icon={Layers} count={pillars.length}
                          sectionKey="content_pillars"
                          sectionMeta={strategy.section_meta}
                          onEdit={openEdit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {pillars.map((p, i) => (
                    <div key={i} className="border border-border p-2 bg-card">
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-semibold text-sm truncate">{p.name}</span>
                        <Badge variant="outline" className="rounded-none text-[10px] font-mono">
                          {p.share_pct}%
                        </Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                        {p.description}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Topics */}
              <Section title="Proposed topics" icon={Target} count={topics.length}
                       sectionKey="proposed_topics"
                       sectionMeta={strategy.section_meta}
                       onEdit={openEdit}
                       action={<span className="text-[10px] text-muted-foreground">
                                    Content Bucket generation will select from this list
                                </span>}>
                <div className="border border-border bg-card max-h-[500px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Topic</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Pillar</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Stage</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Formats</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Persona</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topics.map((t, i) => (
                        <tr key={i} className="border-b border-border hover:bg-muted/20"
                              data-testid={`content-strategy-topic-${i}`}>
                          <td className="px-2 py-1.5 text-xs">
                            <div className="font-medium">{t.title}</div>
                            {t.hook && <div className="text-[10px] text-muted-foreground mt-0.5 italic">{t.hook}</div>}
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{t.pillar || "—"}</td>
                          <td className="px-2 py-1.5">
                            <span className={`text-[9px] uppercase border px-1 py-0.5 font-bold ${STAGE_TONE[t.funnel_stage] || ""}`}>
                              {t.funnel_stage || "—"}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                            {(t.formats || []).slice(0, 3).map((f, j) => (
                              <span key={j} className="inline-block border border-border bg-muted/30 px-1 mr-0.5 mb-0.5 font-mono">{f}</span>
                            ))}
                            {(t.formats || []).length > 3 && (
                              <span className="text-muted-foreground">+{t.formats.length - 3}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-muted-foreground truncate max-w-[140px]">{t.target_persona || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              {/* Recommendations */}
              <Section title="Strategic recommendations" icon={Info}
                          sectionKey="recommendations"
                          sectionMeta={strategy.section_meta}
                          onEdit={openEdit}>
                {(strategy.recommendations || []).length > 0 ? (
                  <ul className="space-y-1.5 text-sm">
                    {strategy.recommendations.map((r, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-primary font-bold">→</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground italic">No recommendations yet</div>
                )}
              </Section>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Content mix pie */}
              <Section title="Content mix" icon={PieChart}
                          sectionKey="content_mix"
                          sectionMeta={strategy.section_meta}
                          onEdit={openEdit}>
                {Object.keys(contentMix).length > 0 ? (
                  <ContentMixBar mix={contentMix} />
                ) : (
                  <div className="text-xs text-muted-foreground italic">No content mix set</div>
                )}
              </Section>

              {/* Cadence */}
              <Section title="Cadence" icon={ClipboardList}
                          sectionKey="cadence"
                          sectionMeta={strategy.section_meta}
                          onEdit={openEdit}>
                {Object.keys(cadence).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(cadence).map(([channel, c]) => (
                      <div key={channel} className="flex items-center justify-between text-xs border-b border-border/50 py-1">
                        <span className="font-mono">{channel}</span>
                        <span className="text-muted-foreground">
                          {c.per_week ? `${c.per_week}/wk` : c.per_month ? `${c.per_month}/mo` : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">No cadence set</div>
                )}
              </Section>

              {/* Hashtags */}
              <Section title="Hashtag bank" icon={Hash}
                          sectionKey="hashtag_bank"
                          sectionMeta={strategy.section_meta}
                          onEdit={openEdit}>
                {Object.keys(hashtagBank).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(hashtagBank).map(([pillar, tags]) => (
                      <div key={pillar}>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                          {pillar}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(tags || []).slice(0, 12).map((t, i) => (
                            <span key={i} className="text-[10px] font-mono bg-muted/40 border border-border px-1">{t}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">No hashtags yet</div>
                )}
              </Section>

              {/* Key dates */}
              <Section title="Dates to watch" icon={PenSquare}
                          sectionKey="key_dates_to_watch"
                          sectionMeta={strategy.section_meta}
                          onEdit={openEdit}>
                {(strategy.key_dates_to_watch || []).length > 0 ? (
                  <ul className="space-y-1.5 text-xs">
                    {strategy.key_dates_to_watch.map((d, i) => (
                      <li key={i} className="border-l-2 border-primary pl-2">
                        <div className="font-semibold text-[11px]">{d.date_hint}</div>
                        <div className="text-muted-foreground">{d.angle}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground italic">No dates flagged</div>
                )}
              </Section>
            </div>
          </div>
        </>
      )}

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg" data-testid="content-strategy-generate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Generate content strategy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Number of topics (10-60)</Label>
              <Input type="number" min={10} max={60} className="h-9 rounded-none"
                       value={genForm.num_topics}
                       onChange={(e) => setGenForm({ ...genForm, num_topics: Math.max(10, Math.min(60, +e.target.value || 30)) })}
                       data-testid="content-strategy-num-topics" />
            </div>
            <div className="flex items-center justify-between border border-border p-3">
              <div>
                <div className="text-sm font-semibold">Include competitor snapshot</div>
                <div className="text-[10px] text-muted-foreground">Uses the latest AI compare-snapshot for signal.</div>
              </div>
              <Switch checked={genForm.include_competitor_snapshot}
                        onCheckedChange={(v) => setGenForm({ ...genForm, include_competitor_snapshot: v })}
                        data-testid="content-strategy-include-competitor" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Custom instructions (optional)</Label>
              <Textarea rows={4} placeholder="e.g. Push more BOFU. Focus on Tier-1 SaaS decision makers. Avoid holiday content."
                          value={genForm.custom_instructions}
                          onChange={(e) => setGenForm({ ...genForm, custom_instructions: e.target.value })}
                          className="rounded-none"
                          data-testid="content-strategy-custom-instructions" />
              <div className="text-[10px] text-muted-foreground mt-1">
                These take the HIGHEST priority in the LLM prompt.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={generate} disabled={busy}
                      data-testid="content-strategy-generate-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {busy ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refine dialog */}
      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="max-w-lg" data-testid="content-strategy-refine-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-primary" /> Refine strategy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-eyebrow mb-1 inline-block">What should change?</Label>
            <Textarea rows={6} value={refineInput}
                        onChange={(e) => setRefineInput(e.target.value)}
                        placeholder="e.g. Drop the seasonal-holiday pillar. Add more topics on API integrations. Skew MOFU heavier. Include a 'customer story' theme."
                        className="rounded-none"
                        data-testid="content-strategy-refine-input" />
            <div className="text-[10px] text-muted-foreground">
              Creates a new version rooted in the current one — nothing lost.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setRefineOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={refine} disabled={busy}
                      data-testid="content-strategy-refine-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {busy ? "Refining…" : "Refine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl" data-testid="content-strategy-versions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-primary" /> Strategy versions
            </DialogTitle>
          </DialogHeader>
          <div className="border border-border max-h-[440px] overflow-auto">
            {versions.map((v) => {
              const isCurrent = v.id === strategy?.id;
              return (
                <div key={v.id}
                       className={`flex items-center gap-2 border-b border-border p-2 ${isCurrent ? "bg-primary/5" : ""}`}
                       data-testid={`content-strategy-version-${v.version}`}>
                  <Badge variant="outline" className="rounded-none text-[10px]">v{v.version}</Badge>
                  <span className={`text-[10px] uppercase border px-1 ${STATUS_TONE[v.status] || ""}`}>{v.status}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">
                      {v.summary || `${v.num_topics_requested || 0} topics`}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {fmtRelative(v.created_at)} · {v.created_by_name || "—"} · {v.generation_source}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-none h-6 text-[10px]"
                            onClick={() => openVersion(v.id)}
                            data-testid={`content-strategy-version-open-${v.version}`}>Open</Button>
                  <button type="button" className="p-1 text-muted-foreground hover:text-rose-600"
                            onClick={() => delVersion(v.id)}
                            data-testid={`content-strategy-version-delete-${v.version}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setVersionsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick edit section dialog */}
      {editSection && strategy && (
        <QuickEditDialog
          open={!!editSection}
          onOpenChange={(v) => { if (!v) setEditSection(null); }}
          wsId={wsId} bid={bid} sid={strategy.id}
          section={editSection}
          current={strategy[editSection]}
          onSaved={handleSectionSaved}
        />
      )}

      {/* Revision history */}
      {strategy && (
        <RevisionHistoryDialog
          open={revisionOpen}
          onOpenChange={setRevisionOpen}
          wsId={wsId} bid={bid} sid={strategy.id}
        />
      )}

      {/* Carry-over merge dialog */}
      {strategy && (
        <CarryOverDialog
          open={carryOverOpen}
          onOpenChange={setCarryOverOpen}
          wsId={wsId} bid={bid}
          currentStrategy={strategy}
          versions={versions}
          onDone={(doc) => { setStrategy(doc); loadAll(); }}
        />
      )}
    </div>
  );
}

function Section({ title, icon: Icon, count, action, children,
                       sectionKey, sectionMeta, onEdit }) {
  return (
    <div className="border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
        {Icon && <Icon className="w-3.5 h-3.5 text-primary" />}
        <span className="text-[10px] uppercase tracking-widest font-semibold">{title}</span>
        {count !== undefined && count !== null && (
          <Badge variant="outline" className="rounded-none text-[10px]">{count}</Badge>
        )}
        {sectionKey && sectionMeta && (
          <SectionSourceBadge meta={sectionMeta[sectionKey]} />
        )}
        <div className="ml-auto flex items-center gap-2">
          {action}
          {sectionKey && onEdit && (
            <button type="button"
                        onClick={() => onEdit(sectionKey)}
                        className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1 border border-transparent hover:border-primary/60 px-1 py-0.5"
                        title={`Edit ${title}`}
                        data-testid={`section-edit-${sectionKey}`}>
              <PenSquare className="w-3 h-3" /> Edit
            </button>
          )}
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone = "" }) {
  return (
    <div className="border border-border bg-card p-2">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-xl font-mono font-black mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

function ContentMixBar({ mix }) {
  const total = Object.values(mix).reduce((a, b) => a + (Number(b) || 0), 0) || 1;
  const colors = ["bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500", "bg-rose-500", "bg-indigo-500"];
  return (
    <div>
      <div className="h-3 flex border border-border overflow-hidden">
        {Object.entries(mix).map(([k, v], i) => (
          <div key={k} className={colors[i % colors.length]}
                 style={{ width: `${(v / total) * 100}%` }}
                 title={`${k}: ${v}%`} />
        ))}
      </div>
      <div className="mt-2 space-y-1">
        {Object.entries(mix).map(([k, v], i) => (
          <div key={k} className="flex items-center gap-2 text-[11px]">
            <span className={`w-2 h-2 ${colors[i % colors.length]}`} />
            <span className="flex-1 capitalize">{k.replaceAll("_", " ")}</span>
            <span className="font-mono">{v}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
